// We need a token to be supplied to be able to query data
// If the user is logged in and has smtzAuth then we use it
// If the user is logged in but does nt have smtzAuth then we request it

import { setupServices } from "@/services";
import { dexieDB } from "@/services/dexie-database";
import { LogService } from "@/services/logging";
import AuthModule from "@/store/modules/auth";
import AppModule from "@/store/modules/app";
import BookingModule from "@/store/modules/booking";
import CashFloatModule from "@/store/modules/cash-float";
import CustomersModule from "@/store/modules/customers";
import ModalModule from "@/store/modules/modal";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { getSmtzAuth, getUserAccessToken } from "./helpers/auth";
import { initStoreModules, restoreAllStoreModules } from "./helpers/store";
import { SharedCallbacks } from "./shared-resources";
import qs from "qs-lite";

/**
 * Check if the current window is a customer display window.
 */
export function isCustomerDisplayWindow(): boolean {
  return window.location.pathname.includes("customer-display");
}

export function appSetup() {
  setupApollo();
  setupServices();
  initStoreModules();
  setupRequest();
  setupUserInfo();
  setupPOSConfig();
  setupUserLogout();
  setupModalSetStatus();
  setupCashfloat();
  setupCustomerInfo();
  setupGetSelectedCustomerId();
  setupGetSelectedCustomerEmail();
  setupGetSelectedCustomerLastName();
  setupGetSelectedCustomerFirstName();
  setupResponse();
  setupConsoleLog();
}

/**
 * Setup for customer display window.
 * Same as appSetup() but with minimal setup:
 *  - no sync triggers, no watchEffects, no rxdb replication, etc.
 */
export function customerDisplaySetup() {
  // Only restore store state, no watchEffects for persistence
  restoreAllStoreModules();
  setupRequest();
  setupResponse();
  setupUserInfo();
  setupPOSConfig();
  setupUserLogout();
  setupModalSetStatus();

  // Open Dexie DB asynchronously (non-blocking)
  setupDexieDatabase(); // Used in Customer Display
}

/**
 * Opens Dexie database asynchronously.
 * Called after synchronous setup to avoid blocking route guards.
 */
async function setupDexieDatabase() {
  try {
    if (!dexieDB.isOpen()) {
      await dexieDB.open();
    }
  } catch (error) {
    LogService.info("Error opening Dexie DB", error);
  }
}

function setupConsoleLog() {
  const originalLog = console.log;
  console.log = (...args: any[]): void => {
    const loggingEnabled =
      AppModule.debugMode ||
      POSConfigModule.groupId === 1 ||
      POSConfigModule.groupId === 19066 ||
      POSConfigModule.isLocarno ||
      import.meta.env.VITE_ENV === "test" ||
      import.meta.env.VITE_ENV === "dev";
    if (!loggingEnabled) {
      return;
    }
    originalLog.apply(console, args);
  };
}

export function setupApollo() {
  SharedCallbacks.gqlAuthHeader = getSmtzAuth;
}

export function setupRequest() {
  SharedCallbacks.authHeaderCallback = authBuilder;
  SharedCallbacks.startTimeMetadataCallback = startTimeMetadataBuilder;
}

export function setupResponse() {
  SharedCallbacks.monitorLatencyCallback = getResponseLatency;
}

export function authBuilder(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  return new Promise((resolve, reject) => {
    const authorization = config.headers?.Authorization;
    const cookieToken = getUserAccessToken();

    if (AuthModule.token || cookieToken) {
      config.headers.set("Authorization", authorization || String(cookieToken));
      resolve(config);
    } else {
      reject(new Error("No token found in AuthModule"));
    }
  });
}

export function startTimeMetadataBuilder(config: InternalAxiosRequestConfig) {
  // Capture the request time and store it in the request metadata
  (config as any).metadata = { requestStartTime: Date.now() };
}

export function getResponseLatency(
  response: AxiosResponse,
  error?: AxiosError
) {
  // Calculate the latency
  // make sure that we don't log the latency when the base url is the log server to avoid an infinite loop
  if (
    (response.config as any).metadata?.requestStartTime &&
    response.config.baseURL !== import.meta.env.VITE_LOG_API
  ) {
    const requestStartTime = (response.config as any).metadata.requestStartTime;
    const latency = Date.now() - requestStartTime;
    const method = (response.config.method || "UNKNOWN").toUpperCase();
    const queryString = response.config.params
      ? `?${qs.stringify(response.config.params)}`
      : "";
    const url = response.config.url + queryString;

    if (error) {
      LogService.info(
        `${method} Request for ${url} failed after ${latency}ms`,
        { error }
      );
    } else {
      LogService.info(`${method} Request for ${url} took ${latency}ms`);

      // Monitoring Adyen Terminal slow connectivity calls
      if (
        url.includes("connected-terminals?uniqueTerminalId") &&
        latency > 1000
      ) {
        // Lets throw an alert
        const match = url.match(/uniqueTerminalId=[^&]+/);
        LogService.info(
          `Slow Terminal Connectivity Captured (${latency}ms): ${
            match ? match[0] : "terminalId Not Found"
          }`
        );
      }
    }
  }
}

export function setupUserInfo() {
  SharedCallbacks.getSellerId = () => {
    const sellerId = UserModule.userId;
    if (!sellerId) {
      throw new Error("No seller id on app");
    }

    return sellerId;
  };
}

export function setupPOSConfig() {
  SharedCallbacks.getPOSConfigId = () => {
    const posConfig = POSConfigModule.selectedConfig;
    if (!posConfig) {
      throw new Error("No POS Config set");
    }

    return posConfig.id;
  };

  SharedCallbacks.getGroupInfo = () => {
    const groupInfo = POSConfigModule.groupInfo;
    if (!groupInfo) {
      throw new Error("No group info set");
    }

    return groupInfo;
  };
}

export function setupUserLogout() {
  SharedCallbacks.UserLogOut = AuthModule.UserLogOut;
}

export function setupModalSetStatus() {
  SharedCallbacks.ModalSetStatus = ModalModule.setStatus;
}

export function setupCashfloat() {
  SharedCallbacks.cashfloat = () => {
    const cashfloat = CashFloatModule.cashFloat;
    if (!cashfloat) {
      return null;
    }

    return cashfloat;
  };
}

export function setupCustomerInfo() {
  SharedCallbacks.setCustomerInfo = BookingModule.setCustomerData;
}

export function setupGetSelectedCustomerId() {
  SharedCallbacks.getSelectedCustomerId = () => {
    const id = CustomersModule.getSelectedCustomerId;
    return id;
  };
}

export function setupGetSelectedCustomerEmail() {
  SharedCallbacks.getSelectedCustomerEmail = () => {
    const email = CustomersModule.getSelectedCustomerEmail;

    return email;
  };
}

export function setupGetSelectedCustomerLastName() {
  SharedCallbacks.getSelectedCustomerLastName = () => {
    const lastname = CustomersModule.getSelectedCustomerLastName;

    return lastname;
  };
}

export function setupGetSelectedCustomerFirstName() {
  SharedCallbacks.getSelectedCustomerFirstName = () => {
    const firstname = CustomersModule.getSelectedCustomerFirstName;

    return firstname;
  };
}
